class MongoRealService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_ATLAS_DATA_API_URL;
    this.appId = process.env.REACT_APP_ATLAS_APP_ID;
    this.dataSource = 'Cluster0';
    this.database = process.env.REACT_APP_DATABASE_NAME || 'timesheet_db';
    this.apiKey = process.env.REACT_APP_ATLAS_API_KEY;
    this.isInitialized = false;
  }

  async connect() {
    if (this.isInitialized) {
      return;
    }

    if (!this.apiKey || this.apiKey === 'YOUR_MONGODB_ATLAS_API_KEY_HERE') {
      throw new Error('MongoDB Atlas API key is required. Please set REACT_APP_ATLAS_API_KEY in your .env file.');
    }

    if (!this.baseUrl) {
      throw new Error('MongoDB Atlas Data API URL is required. Please set REACT_APP_ATLAS_DATA_API_URL in your .env file.');
    }

    this.isInitialized = true;
  }

  async makeApiRequest(endpoint, method = 'POST', data = null) {
    await this.connect();

    const url = `${this.baseUrl}/${endpoint}`;
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'api-key': this.apiKey
      }
    };

    if (data) {
      options.body = JSON.stringify({
        dataSource: this.dataSource,
        database: this.database,
        ...data
      });
    }

    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorData.error || 'Unknown error'}`);
    }

    return response.json();
  }

  // Employee methods
  async authenticateEmployee(email, password) {
    try {
      const result = await this.makeApiRequest('action/findOne', 'POST', {
        collection: 'employees',
        filter: {
          email: email.toLowerCase(),
          password: password,
          isActive: true
        }
      });

      if (!result.document) {
        throw new Error('Invalid credentials');
      }

      const employee = result.document;
      return {
        id: employee._id,
        name: employee.name,
        email: employee.email,
        department: employee.department,
        position: employee.position,
        role: employee.role || 'employee'
      };
    } catch (error) {
      console.error('Authentication error:', error);
      throw error;
    }
  }

  async getEmployees() {
    try {
      const result = await this.makeApiRequest('action/find', 'POST', {
        collection: 'employees',
        filter: {},
        sort: { name: 1 }
      });

      return result.documents.map(emp => ({
        _id: emp._id,
        name: emp.name,
        email: emp.email,
        department: emp.department,
        position: emp.position,
        isActive: emp.isActive,
        role: emp.role || 'employee'
      }));
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  }

  async createEmployee(employeeData) {
    try {
      const newEmployee = {
        ...employeeData,
        email: employeeData.email.toLowerCase(),
        role: employeeData.role || 'employee',
        isActive: employeeData.isActive !== undefined ? employeeData.isActive : true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const result = await this.makeApiRequest('action/insertOne', 'POST', {
        collection: 'employees',
        document: newEmployee
      });

      return {
        _id: result.insertedId,
        ...newEmployee
      };
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  }

  async updateEmployee(employeeId, updateData) {
    try {
      const updateDoc = {
        ...updateData,
        updatedAt: new Date()
      };

      if (updateData.email) {
        updateDoc.email = updateData.email.toLowerCase();
      }

      const result = await this.makeApiRequest('action/updateOne', 'POST', {
        collection: 'employees',
        filter: { _id: { $oid: employeeId } },
        update: { $set: updateDoc }
      });

      if (result.matchedCount === 0) {
        throw new Error('Employee not found');
      }

      return updateDoc;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  }

  async deleteEmployee(employeeId) {
    try {
      const result = await this.makeApiRequest('action/deleteOne', 'POST', {
        collection: 'employees',
        filter: { _id: { $oid: employeeId } }
      });

      if (result.deletedCount === 0) {
        throw new Error('Employee not found');
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting employee:', error);
      throw error;
    }
  }

  // Project methods
  async getProjects() {
    try {
      const result = await this.makeApiRequest('action/find', 'POST', {
        collection: 'projects',
        filter: {},
        sort: { name: 1 }
      });

      return result.documents;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  }

  async createProject(projectData) {
    try {
      const newProject = {
        ...projectData,
        status: projectData.status || 'active',
        isActive: projectData.isActive !== undefined ? projectData.isActive : true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const result = await this.makeApiRequest('action/insertOne', 'POST', {
        collection: 'projects',
        document: newProject
      });

      return {
        _id: result.insertedId,
        ...newProject
      };
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  async updateProject(projectId, updateData) {
    try {
      const updateDoc = {
        ...updateData,
        updatedAt: new Date()
      };

      const result = await this.makeApiRequest('action/updateOne', 'POST', {
        collection: 'projects',
        filter: { _id: { $oid: projectId } },
        update: { $set: updateDoc }
      });

      if (result.matchedCount === 0) {
        throw new Error('Project not found');
      }

      return updateDoc;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  async deleteProject(projectId) {
    try {
      const result = await this.makeApiRequest('action/deleteOne', 'POST', {
        collection: 'projects',
        filter: { _id: { $oid: projectId } }
      });

      if (result.deletedCount === 0) {
        throw new Error('Project not found');
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  // Timesheet methods
  async submitTimesheet(timesheetData) {
    try {
      const newTimesheet = {
        ...timesheetData,
        date: new Date(timesheetData.date),
        status: 'submitted',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const result = await this.makeApiRequest('action/insertOne', 'POST', {
        collection: 'timesheets',
        document: newTimesheet
      });

      return {
        _id: result.insertedId,
        ...newTimesheet
      };
    } catch (error) {
      console.error('Error submitting timesheet:', error);
      throw error;
    }
  }

  async getTimesheets(employeeId, startDate = null, endDate = null) {
    try {
      let filter = {};
      
      if (employeeId) {
        filter.employeeId = employeeId;
      }
      
      if (startDate || endDate) {
        filter.date = {};
        if (startDate) filter.date.$gte = new Date(startDate);
        if (endDate) filter.date.$lte = new Date(endDate);
      }

      const result = await this.makeApiRequest('action/find', 'POST', {
        collection: 'timesheets',
        filter: filter,
        sort: { date: -1 }
      });

      return result.documents;
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      throw error;
    }
  }

  async getAllTimesheets(startDate = null, endDate = null) {
    return this.getTimesheets(null, startDate, endDate);
  }


  // Initialize database with admin user if needed
  async seedInitialData() {
    try {
      // Check if admin user exists
      const adminCheck = await this.makeApiRequest('action/findOne', 'POST', {
        collection: 'employees',
        filter: { email: 'admin@company.com' }
      });

      if (!adminCheck.document) {
        // Create admin user
        await this.createEmployee({
          name: 'Admin User',
          email: 'admin@company.com',
          password: 'admin123',
          department: 'Administration',
          position: 'System Administrator',
          role: 'admin',
          isActive: true
        });
      }

      // Check if any sample employees exist
      const employeeCount = await this.makeApiRequest('action/find', 'POST', {
        collection: 'employees',
        filter: { role: 'employee' }
      });

      if (employeeCount.documents.length === 0) {
        // Create sample employees
        const sampleEmployees = [
          {
            name: 'John Doe',
            email: 'john@company.com',
            password: 'password123',
            department: 'Engineering',
            position: 'Senior Developer',
            role: 'employee'
          },
          {
            name: 'Jane Smith',
            email: 'jane@company.com',
            password: 'password123',
            department: 'Design',
            position: 'UI/UX Designer',
            role: 'employee'
          }
        ];

        for (const emp of sampleEmployees) {
          await this.createEmployee(emp);
        }
      }

      // Check if any projects exist
      const projectCount = await this.makeApiRequest('action/find', 'POST', {
        collection: 'projects',
        filter: {}
      });

      if (projectCount.documents.length === 0) {
        // Create sample projects
        const sampleProjects = [
          {
            name: 'Website Redesign',
            description: 'Complete redesign of company website',
            client: 'Internal',
            status: 'active'
          },
          {
            name: 'Mobile App Development',
            description: 'New mobile application for customers',
            client: 'ABC Corp',
            status: 'active'
          }
        ];

        for (const proj of sampleProjects) {
          await this.createProject(proj);
        }
      }

    } catch (error) {
      console.error('Error seeding initial data:', error);
      throw error;
    }
  }
}

// Create singleton instance
const mongoRealService = new MongoRealService();

export default mongoRealService;
